<template>
    <div class="prefixCls" :style="{ width: containerWidth }">
        <textarea :id="tinymceId" ref="elRef" :style="{ visibility: 'hidden' }"></textarea>
    </div>
</template>

<script setup lang="ts">
import tinymce, { Editor } from 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default/icons'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/autosave'
import 'tinymce/plugins/code'
import 'tinymce/plugins/codesample'
import 'tinymce/plugins/directionality'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/hr'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/media'
import 'tinymce/plugins/nonbreaking'
import 'tinymce/plugins/noneditable'
import 'tinymce/plugins/pagebreak'
import 'tinymce/plugins/paste' // 粘贴
import 'tinymce/plugins/preview'
import 'tinymce/plugins/print'
import 'tinymce/plugins/save'
import 'tinymce/plugins/searchreplace'
// import 'tinymce/plugins/spellchecker'
import 'tinymce/plugins/tabfocus'
import 'tinymce/plugins/template'
import 'tinymce/plugins/textpattern'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/visualchars'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/image'
// import 'tinymce/plugins/quickbars'; //快速工具栏 
// import 'tinymce/plugins/table';

import { computed, nextTick, ref, unref, watch, onDeactivated, onBeforeUnmount,  getCurrentInstance } from 'vue'
import { toolbar, plugins } from './tinymce'
import { buildShortUUID, isNumber } from '@/utils/tinymce.ts'
import { bindHandlers } from './helper'
import { onMountedOrActivated } from '@/utils/onMountedOrActivated'
import { message } from 'ant-design-vue'


const props = defineProps({
    options: {
        type: Object,
        default: () => { }
    },
    value: {
        type: String
    },
    toolbar: {
        type: Array,
        default: toolbar
    },
    plugins: {
        type: Array,
        default: plugins
    },
    modelValue: {
        type: String
    },
    height: {
        type: [Number, String],
        required: false,
        default: 400
    },
    width: {
        type: [Number, String],
        required: false,
        default: 'auto'
    },
    showImageUpload: {
        type: Boolean,
        default: true
    },
    // openModal: {
    //     type: Function,
    //     default: () => ({})
    // }
})
const emits = defineEmits(['change', 'update:modelValue', 'inited', 'init-error', 'openModal'])
const { attrs } = getCurrentInstance()
const tinymceId = ref(buildShortUUID('tiny-vue'))
const containerWidth = computed(() => {
    const width = props.width
    if (isNumber(width)) {
        return `${width}px`
    }
    return width
})
const editorRef = ref<Editor>(null)
const fullscreen = ref(false)
const elRef = ref(null)
const tinymceContent = computed(() => props.modelValue)

const initOptions = computed(() => {
    const { height, options, toolbar, plugins } = props
    const publicPath = '/'
    return {
        selector: `#${unref(tinymceId)}`,
        height,
        menubar: false,
        // menubar: 'file edit insert view format table',
        toolbar,
        plugins,
        inline: false,
        fontsize_formats: '12px 14px 16px 18px 24px 36px 48px 56px 72px',
        language_url: '/resource/tinymce/langs/zh_CN.js',
        language: 'zh_CN',
        branding: false,
        default_link_target: '_blank',
        link_title: false,
        object_resizing: false,
        auto_focus: true,
        skin: 'oxide',
        skin_url: '/resource/tinymce/skins/ui/oxide',
        content_css: '/resource/tinymce/skins/ui/oxide/content.min.css',

        typeahead_urls: false,
        // paste_data_images: true, // 此选项指定是否应从粘贴的内容中删除data:url图像（内联图像）。
        // 键盘Ctrl + V 粘贴后的过滤操作，禁止图片
        // paste_preprocess: function (plugin, args) {
        //     const isImg = args.content.startsWith('<img') && args.content.endsWith('/>') && args.content.includes('src=')
        //     isImg && (args.content = '',message.info('请点击插入图片按钮进行手动上传图片！'))
        // },
        content_style: "img {max-width:50%;}",
        ...options,
        setup: (editor: any) => {
            editorRef.value = editor
            // 注册一个自定义的按钮
            editor.ui.registry.addButton('Upload', {
                icon: 'image',
                tooltip: '插入图片',
                onAction: function (_) {
                    editorRef.value = editor;
                    emits('openModal', true);
                },
            });
            editor.on('init', (e) => initSetup(e))
        }
    }
})

const disabled = computed(() => {
    const { options } = props
    const getdDisabled = options && Reflect.get(options, 'readonly')
    const editor = unref(editorRef)
    if (editor) {
        editor.setMode(getdDisabled ? 'readonly' : 'design')
    }
    return getdDisabled ?? false
})

watch(
    () => attrs.disabled,
    () => {
        const editor = unref(editorRef)
        if (!editor) {
            return
        }
        editor.setMode(attrs.disabled ? 'readonly' : 'design')
    }
)

onMountedOrActivated(() => {
    if (!initOptions.value.inline) {
        tinymceId.value = buildShortUUID('tiny-vue')
    }
    nextTick(() => {
        setTimeout(() => {
            initEditor()
        }, 30)
    })
})

onBeforeUnmount(() => {
    destory()
})

onDeactivated(() => {
    destory()
})

function destory() {
    if (tinymce !== null) {
        tinymce?.remove?.(unref(initOptions).selector!);
    }
}

function initSetup(e) {
    const editor = unref(editorRef)
    if (!editor) {
        return
    }
    const value = props.modelValue || ''

    editor.setContent(value)
    bindModelHandlers(editor)
    bindHandlers(e, attrs, unref(editorRef))

    // document.addEventListener('paste', function (e) {
    //     e.preventDefault();
    // });
}

function initEditor() {
    const el = unref(elRef)
    if (el) {
        el.style.visibility = ''
    }
    tinymce
        .init(unref(initOptions))
        .then((editor: Editor[]) => {
            emits('inited', editor)
        })
        .catch((err) => {
            emits('init-error', err)
        })
}

function setValue(editor, val, prevVal) {
    if (
        editor &&
        typeof val === 'string' &&
        val !== prevVal &&
        val !== editor.getContent({ format: attrs.outputFormat })
    ) {
        editor.setContent(val)
    }
}

function bindModelHandlers(editor) {
    const modelEvents = attrs.modelEvents ? attrs.modelEvents : null
    const normalizedEvents = Array.isArray(modelEvents) ? modelEvents.join(' ') : modelEvents

    watch(
        () => props.modelValue,
        (val, prevVal) => {
            setValue(editor, val, prevVal)
        }
    )

    watch(
        () => props.value,
        (val, prevVal) => {
            setValue(editor, val, prevVal)
        },
        {
            immediate: true
        }
    )

    editor.on(normalizedEvents || 'change keyup undo redo', () => {
        const content = editor.getContent({ format: attrs.outputFormat })
        emits('update:modelValue', content)
        emits('change', content)
    })

    editor.on('FullscreenStateChanged', (e) => {
        fullscreen.value = e.state
    })
}

function handleImageUploading(name) {
    const editor = unref(editorRef)
    if (!editor) {
        return
    }
    editor.execCommand('mceInsertContent', false, getUploadingImgName(name))
    const content = editor?.getContent() ?? ''
    setValue(editor, content)
}

function handleDone(name, url) {
    const editor = unref(editorRef)
    if (!editor) {
        return
    }
    const content = editor?.getContent() ?? ''
    const val = content?.replace(getUploadingImgName(name), `<img src="${url}"/>`) ?? ''
    setValue(editor, val)
}

function getUploadingImgName(name) {
    return `[uploading:${name}]`
}
</script>

<style lang="less" scoped>
.prefixCls {
    position: relative;
    line-height: normal;
}

textarea {
    z-index: -1;
    visibility: hidden;
}
</style>
